import Decimal from 'decimal.js';

/**
 * Montants, prix et quantités du module Ventes : toujours des CHAÎNES décimales (contrat ventes § 2), calculées
 * avec une bibliothèque décimale, jamais avec `Number`. Arrondi au demi supérieur (ROUND_HALF_UP), comme le serveur.
 * Le serveur fait foi : les calculs locaux ne servent qu'à un affichage indicatif quand il ne répond pas.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -40, toExpPos: 40 });

/** Décimal partagé (mêmes réglages) pour les calculs d'argent des écrans : jamais de `Number` sur un montant. */
export const Dec = D;

const SPACES = /[\s  ']/g;

/** Limites du contrat : `Quantity` (15 + 3), `UnitPrice` (14 + 4), `Percent` (3 + 2), taux de TVA (3 + 4). */
export const LIMITS = {
  quantity: { int: 15, dec: 3 },
  unitPrice: { int: 14, dec: 4 },
  percent: { int: 3, dec: 2 },
  taxRate: { int: 3, dec: 4 },
  /** Montant d'un encaissement, d'une imputation, d'un comptage (contrat `PositiveMoney` : 16 + 2). */
  amount: { int: 16, dec: 2 },
} as const;

/** Séparateur décimal de la langue (« , » en français, « . » en anglais). */
export function decimalSeparator(locale: string): ',' | '.' {
  const sep = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value;
  return sep === ',' ? ',' : '.';
}

/**
 * Saisie ambiguë : un séparateur suivi d'exactement 3 chiffres qui n'est pas le séparateur décimal de la langue
 * (« 12.500 » en français, « 1,500 » en anglais) peut se lire 12 500 ou 12,5. Elle est refusée plutôt que devinée ;
 * les espaces des milliers (« 12 500 ») restent toujours acceptés.
 */
export function isAmbiguousDecimal(input: string, locale: string): boolean {
  const other = decimalSeparator(locale) === ',' ? '.' : ',';
  return new RegExp(`\\${other}\\d{3}(?!\\d)`).test(input.replace(SPACES, ''));
}

/**
 * Saisie (« 1 500,75 », « 1500.75 ») → chaîne décimale canonique (« 1500.75 »).
 * Vide → null ; saisie impossible (lettres, signe, trop de décimales ou de chiffres) ou ambiguë → undefined.
 * `locale` : à passer pour toute SAISIE (contrôle d'ambiguïté) ; absent pour une valeur venue du serveur.
 */
export function parseDecimal(input: string, limits: { int: number; dec: number }, locale?: string): string | null | undefined {
  if (locale && isAmbiguousDecimal(input, locale)) return undefined;
  const compact = input.replace(SPACES, '').replace(',', '.');
  if (!compact) return null;
  const re = new RegExp(`^\\d{1,${limits.int}}(\\.\\d{0,${limits.dec}})?$`);
  if (!re.test(compact)) return undefined;
  return new D(compact.endsWith('.') ? compact.slice(0, -1) : compact).toFixed();
}

/** Problème d'une saisie décimale, pour choisir le message : `ambiguous`, `invalid`, ou null (vide ou valide). */
export function decimalProblem(input: string, limits: { int: number; dec: number }, locale: string): 'ambiguous' | 'invalid' | null {
  if (isAmbiguousDecimal(input, locale)) return 'ambiguous';
  return parseDecimal(input, limits) === undefined ? 'invalid' : null;
}

/** Valeur du serveur (« 12.1250 ») → texte de saisie dans la langue (« 12,125 » en français), jamais ambigu. */
export function toInputDecimal(value: string | null | undefined, locale: string): string {
  if (value === null || value === undefined || value.trim() === '') return '';
  let canon: string;
  try {
    canon = new D(value.trim()).toFixed();
  } catch {
    return value;
  }
  return decimalSeparator(locale) === ',' ? canon.replace('.', ',') : canon;
}

/** Nombre de décimales d'une chaîne décimale (« 2.50 » → 2). */
export function decimalsOf(value: string): number {
  const i = value.indexOf('.');
  return i < 0 ? 0 : value.length - i - 1;
}

export function isPositive(value: string | null | undefined): boolean {
  return !!value && new D(value).gt(0);
}

/** Pourcentage de 0 à 100 ? */
export function isPercent(value: string): boolean {
  const d = new D(value);
  return d.gte(0) && d.lte(100);
}

export function sameDecimal(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return (a || null) === (b || null);
  return new D(a).eq(b);
}

/**
 * Affichage localisé d'une chaîne décimale, sans flottant : séparateur de milliers de la langue (espace en
 * français), virgule décimale, `min` à `max` décimales (au-delà : arrondi au demi supérieur).
 */
export function formatDecimal(value: string | null | undefined, locale: string, opts: { min: number; max: number }): string {
  if (value === null || value === undefined || value.trim() === '') return '—';
  let d: Decimal;
  try {
    d = new D(value.trim());
  } catch {
    return value;
  }
  const fixed = d.toDecimalPlaces(opts.max).toFixed(opts.max);
  const negative = fixed.startsWith('-');
  const [int, dec = ''] = (negative ? fixed.slice(1) : fixed).split('.');
  let decimals = dec;
  while (decimals.length > opts.min && decimals.endsWith('0')) decimals = decimals.slice(0, -1);
  const fmt = new Intl.NumberFormat(locale);
  const decimalSep = fmt.formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
  const grouped = fmt.format(BigInt(int!));
  const zero = /^0*$/.test(int! + decimals);
  return `${negative && !zero ? '-' : ''}${grouped}${decimals ? decimalSep + decimals : ''}`;
}

/** Montant (2 décimales) suivi de la devise : « 1 250 000,00 XAF ». */
export function formatMoney(value: string | null | undefined, locale: string, currency?: string | null): string {
  const text = formatDecimal(value, locale, { min: 2, max: 2 });
  return currency && text !== '—' ? `${text} ${currency}` : text;
}

/** Prix unitaire (2 à 4 décimales). */
export function formatUnitPrice(value: string | null | undefined, locale: string, currency?: string | null): string {
  const text = formatDecimal(value, locale, { min: 2, max: 4 });
  return currency && text !== '—' ? `${text} ${currency}` : text;
}

/** Quantité (au plus les décimales de l'unité). */
export function formatQuantity(value: string | null | undefined, locale: string, decimals = 3): string {
  return formatDecimal(value, locale, { min: 0, max: decimals });
}

/** Pourcentage : « 2,5 % ». */
export function formatPercent(value: string | null | undefined, locale: string): string {
  const text = formatDecimal(value, locale, { min: 0, max: 4 });
  return text === '—' ? text : `${text} %`;
}

/** Montants d'une ligne : HT arrondi à 2 décimales PAR LIGNE, puis TVA de la ligne, puis TTC (contrat § 2). */
export function lineAmounts(quantity: string, unitPrice: string, discountPct: string, taxRate: string): { ht: string; tax: string; ttc: string } {
  const ht = new D(quantity)
    .times(unitPrice)
    .times(new D(100).minus(discountPct || '0'))
    .div(100)
    .toDecimalPlaces(2);
  const tax = ht.times(taxRate || '0').div(100).toDecimalPlaces(2);
  return { ht: ht.toFixed(2), tax: tax.toFixed(2), ttc: ht.plus(tax).toFixed(2) };
}

/** Somme de montants déjà arrondis (2 décimales). */
export function sumAmounts(values: string[]): string {
  return values.reduce((acc, v) => acc.plus(v), new D(0)).toFixed(2);
}
